import {
    GetSectionIconUrl,
    GetSectionIconUrlBase64
} from '@assets/component/SectionIcons/SectionIcon';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { ScoreUtils, useScoreStore } from '@assets/model/ScoreUtils';
import { useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import * as style from './ChartStyles.module.less';
import { ChartUtils } from './ChartShared';
import classNames from 'classnames';

interface IChartScoreBreakdownProps {
    reportId: string;
    hideUnderline?: boolean;
}

export const ChartScoreBreakdown = observer((p: IChartScoreBreakdownProps) => {
    const store = useStore();
    const q = store.createDataAccess(p.reportId);

    const hideUnderline = p.hideUnderline === true;
    if (q == null) return null;

    var scoreUtils = new ScoreUtils(q);
    var scoreStore = useScoreStore();
    var groups = scoreStore.SCORE_FIELDS_GROUPS;
    var data = [];

    const GEN_SCORE_ID = 'ALL';
    const scoreTotal = scoreUtils.calcScorePercentile();
    data.push({
        sectionId: GEN_SCORE_ID,
        name: 'Overall Score', //Hack so split ' ' works
        Score: scoreTotal ? (scoreTotal * 100).toFixed(0) : 'N/A'
    });

    groups.forEach(group => {
        if (scoreUtils.is_section_used_for_scoring_by_id(group.sectionId)) {
            data.push({
                sectionId: group.sectionId,
                name: ScoreUtils.get_section_name(group.sectionId),
                Score: (
                    scoreUtils.calcSectionPercentileById(group.sectionId) * 100
                ).toFixed(0)
            });
        }
    });

    const renderCustomAxisTick = ({ x, y, payload }) => {
        const sectionId =
            payload.value == GEN_SCORE_ID ? 'overview' : payload.value;

        //const icon = GetSectionIconUrl(sectionId);
        const icon = GetSectionIconUrlBase64(sectionId);
        const sectionName = data
            .find(section => section.sectionId === payload.value)
            .name.split(' ')[0];
        return (
            <g transform={`translate(${x},${y})`}>
                {/* Text Label */}
                <text
                    fontSize="12"
                    fontFamily="Hanken Grotesk"
                    x={-10}
                    y={5}
                    textAnchor="end"
                    fill="#323B48"
                    fontWeight="700"
                    ref={node => {
                        if (node) {
                            const textWidth = node.getComputedTextLength();
                            // Posiciona o ícone para terminar onde o texto termina (-10)
                            // Como o ícone tem 20px de largura, começamos em -30
                            const iconX = -30;
                            const iconElement =
                                node.parentElement.querySelector('image');
                            if (iconElement) {
                                iconElement.setAttribute('x', String(iconX));
                            }
                        }
                    }}
                >
                    {sectionName}
                </text>
                <image
                    href={icon}
                    x={-30} // Valor inicial, será atualizado pelo ref acima
                    y={-30}
                    width={20}
                    height={20}
                />
            </g>
        );
    };

    const titleCls = classNames(style.title, {
        [style.titleUnderline]: !hideUnderline
    });

    return (
        <Flex direction="column" fillCell={true}>
            <div className={titleCls}>Score Summary</div>
            <ResponsiveContainer id="score-chart" width="100%" height="100%">
                <BarChart
                    layout="vertical"
                    width={500}
                    height={300}
                    data={data}
                    margin={{
                        top: 40,
                        left: 20,
                        right: 20,
                        bottom: 20
                    }}
                    barGap={5}
                    barCategoryGap={10}
                >
                    <CartesianGrid
                        vertical={true}
                        horizontal={false}
                        strokeDasharray="3 3"
                    />
                    <XAxis
                        type="number"
                        domain={[0, 'dataMax']}
                        allowDataOverflow={true}
                        ticks={[0, 25, 50, 75, 100]}
                        tick={{
                            fontSize: 14,
                            fontFamily: 'Hanken Grotesk',
                            fontWeight: 700,
                            fill: '#323B48'
                        }}
                        tickFormatter={value => `${value}%`}
                    />
                    <YAxis
                        dataKey="sectionId"
                        type="category"
                        tick={renderCustomAxisTick}
                    />

                    <Tooltip
                        labelFormatter={value => {
                            return data.find(
                                section => section.sectionId === value
                            ).name;
                        }}
                        formatter={(value, name, props) => `${value}%`}
                    />

                    <Bar dataKey="Score" radius={[0, 4, 4, 0]} maxBarSize={100}>
                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={
                                    entry.sectionId === GEN_SCORE_ID
                                        ? '#A29F93'
                                        : '#133971'
                                }
                            />
                        ))}
                        <LabelList
                            dataKey="Score"
                            position="insideRight"
                            formatter={v => v + '%'}
                            fill="white"
                            style={{
                                fontSize: '14px',
                                fontFamily: 'Hanken Grotesk',
                                fontWeight: 400
                            }}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Flex>
    );
});
